import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, Boxes, Clock, Flame, LayoutGrid, List, Link2, Loader2, Plus,
  RefreshCw, ShieldAlert, Wrench, X, Lock, ArrowRight, UserRound, Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  cmdbApi, itsmApi,
  type CiImpact, type ConfigItem, type ItsmBoardFacets,
  type ItsmCollection, type ItsmItem, type ItsmState,
  type ItsmTransition,
} from '../services/api';
import OpsSchedulePanel from '../components/itsm/OpsSchedulePanel';
import {
  PriorityPill, PrioritySlaPanel, SlaBadge,
} from '../components/itsm/PrioritySla';
import { useSlaPolicy } from '../components/itsm/itsmSla';
import BoardFilters, { type BoardQuery } from '../components/itsm/BoardFilters';
import RaiseTicketDialog from '../components/itsm/RaiseTicketDialog';
import {
  AssignSection, OutcomeField, RelationsSection, TimelineSection,
} from '../components/itsm/TicketWork';
import { ageLabel, heading, muted, primaryButton, ring, secondaryButton } from '../components/itsm/itsmUi';

/**
 * The ITSM board — operations, separate from delivery (PO51-1164).
 *
 * **The columns come from the server**, not from a list in this file. The
 * lifecycles are seeded workflow documents; `itsmApi.describe()` serves them
 * from those same definitions, so a state added or renamed there appears here
 * without a frontend change. A hardcoded column list would be a second copy of
 * the state machine, and the first thing to disagree with the one that actually
 * governs transitions.
 *
 * **Sorted and filtered on the server** (PO46-1280): age, priority and SLA;
 * organisation, queue (the ticket's owner group) and assignee. The board shows
 * the server's order over the whole queue and pages it, so a breaching ticket
 * is never hidden behind a truncated first page.
 *
 * **Moves are transitions, not drag-to-status.** The delivery board drags cards
 * between columns because a delivery status is just a place. An ITIL move is a
 * named act with guards on it — a failed Change cannot be reworked until its
 * root-cause analysis exists — so the card offers the named transitions, and
 * shows the blocked ones WITH THEIR REASON rather than hiding them.
 */

const COLLECTIONS: { id: ItsmCollection; label: string; icon: typeof Flame }[] = [
  { id: 'incidents', label: 'Incidents', icon: Flame },
  { id: 'requests', label: 'Requests', icon: ArrowRight },
  { id: 'problems', label: 'Problems', icon: ShieldAlert },
  { id: 'changes', label: 'Changes', icon: Wrench },
  { id: 'tasks', label: 'Tasks', icon: LayoutGrid },
  { id: 'events', label: 'Events', icon: AlertTriangle },
];

const CATEGORY_DOT: Record<string, string> = {
  todo: 'bg-slate-400',
  in_progress: 'bg-amber-500',
  done: 'bg-green-600',
  cancelled: 'bg-slate-400',
};

const PAGE_SIZE = 200;

const DEFAULT_QUERY: BoardQuery = {
  sort: 'sla', order: '', orgId: '', queue: '', assignee: '', sla: '',
  unassignedService: false, openOnly: true,
};

type Describe = Awaited<ReturnType<typeof itsmApi.describe>>;

export default function ItsmPage() {
  const [collection, setCollection] = useState<ItsmCollection>('incidents');
  const [describe, setDescribe] = useState<Describe | null>(null);
  const [items, setItems] = useState<ItsmItem[]>([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState<ItsmBoardFacets | null>(null);
  const [query, setQuery] = useState<BoardQuery>(DEFAULT_QUERY);
  const [view, setView] = useState<'board' | 'list'>('board');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState<ItsmItem | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    itsmApi.describe().then(setDescribe).catch(() => setDescribe(null));
  }, []);

  const fetchPage = useCallback((offset: number) => itsmApi.list(collection, {
    open: query.openOnly,
    sort: query.sort,
    order: query.order,
    orgId: query.orgId || undefined,
    queue: query.queue || undefined,
    assignee: query.assignee || undefined,
    sla: query.sla,
    unassignedService: query.unassignedService,
    limit: PAGE_SIZE,
    offset,
  }), [collection, query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPage(0);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setFacets(data.facets || null);
    } catch (err: unknown) {
      toast.error((err instanceof Error && err.message) || 'Could not load the board. Refresh to try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  useEffect(() => { void load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(items.length);
      setItems((prev) => [...prev, ...(data.items || [])]);
      setTotal(data.total || 0);
    } catch (err: unknown) {
      toast.error((err instanceof Error && err.message) || 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  };

  const states: ItsmState[] = useMemo(
    () => describe?.collections?.[collection]?.states || [], [describe, collection]);
  // Terminal states are hidden while "open only" is on — otherwise every board
  // grows two permanently-empty columns that push the working ones off-screen.
  const columns = query.openOnly ? states.filter((s) => !s.terminal) : states;

  // Within a column the server's order is kept: grouping does not re-sort.
  const byState = useMemo(() => {
    const map: Record<string, ItsmItem[]> = {};
    states.forEach((s) => { map[s.id] = []; });
    items.forEach((i) => { (map[i.status] = map[i.status] || []).push(i); });
    return map;
  }, [items, states]);

  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (facets?.orgs || []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [facets]);

  const refresh = async (updated?: ItsmItem) => {
    await load();
    if (updated) setSelected(updated);
  };

  const more = total - items.length;

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Wrench size={20} className="text-accent" aria-hidden="true" />
            Service operations
          </h1>
          <p className={`text-sm ${muted} mt-1`}>
            ITIL operations, tracked separately from delivery. Columns and moves
            come from each type's own lifecycle.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => load()} aria-label="Refresh the board"
            className={`p-2 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-accent hover:text-accent ${ring}`}>
            <RefreshCw size={14} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => setCreating(true)}
            className={`${primaryButton} flex items-center gap-1.5`}>
            <Plus size={14} aria-hidden="true" /> Raise
          </button>
        </div>
      </div>

      {/* Type switcher — one board per ITIL type, because their lifecycles are
          genuinely different and a merged board would have to invent a common
          set of columns that matches none of them. */}
      <div className="flex flex-wrap items-center gap-1 mb-3" role="group" aria-label="Ticket type">
        {COLLECTIONS.map((c) => {
          const Icon = c.icon;
          const active = c.id === collection;
          return (
            <button
              key={c.id}
              type="button"
              aria-pressed={active}
              onClick={() => { setCollection(c.id); setSelected(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border ${ring} ${
                active
                  ? 'border-accent text-accent bg-accent/5'
                  : 'border-transparent text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <Icon size={14} aria-hidden="true" /> {c.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-start gap-3">
        <BoardFilters value={query} facets={facets}
          onChange={(patch) => setQuery((q) => ({ ...q, ...patch }))} />
        <div className="flex items-center gap-1 ml-auto self-end mb-4" role="group" aria-label="Layout">
          <button type="button" onClick={() => setView('board')} aria-pressed={view === 'board'}
            aria-label="Board view"
            className={`p-1.5 rounded ${ring} ${view === 'board' ? 'text-accent' : 'text-slate-500 dark:text-slate-400'}`}>
            <LayoutGrid size={16} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => setView('list')} aria-pressed={view === 'list'}
            aria-label="List view"
            className={`p-1.5 rounded ${ring} ${view === 'list' ? 'text-accent' : 'text-slate-500 dark:text-slate-400'}`}>
            <List size={16} aria-hidden="true" />
          </button>
          <span className={`text-xs ${muted} ml-2`} aria-live="polite">
            {items.length < total ? `${items.length} of ${total}` : total}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1" role="status">
          <Loader2 size={22} className="animate-spin motion-reduce:animate-none text-accent" aria-hidden="true" />
          <span className="sr-only">Loading…</span>
        </div>
      ) : view === 'board' ? (
        <div className="flex-1 overflow-x-auto">
          <div className="flex gap-3 h-full min-w-max pb-2">
            {columns.map((s) => (
              <section key={s.id} aria-label={s.label} className="w-64 shrink-0 flex flex-col">
                <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-300">
                  <span className={`w-2 h-2 rounded-full ${CATEGORY_DOT[s.category] || 'bg-slate-400'}`} aria-hidden="true" />
                  {s.label}
                  <span className={`ml-auto ${muted}`}>{(byState[s.id] || []).length}</span>
                </div>
                <div className="flex-1 space-y-2 overflow-y-auto p-1 rounded-lg bg-slate-50 dark:bg-slate-900/60">
                  {(byState[s.id] || []).map((it) => (
                    <Card key={it._id} item={it} orgName={orgName[it.organizationId || '']}
                      onOpen={() => setSelected(it)} />
                  ))}
                  {(byState[s.id] || []).length === 0 && (
                    <p className={`text-[11px] ${muted} px-2 py-3`}>Nothing here.</p>
                  )}
                </div>
              </section>
            ))}
            {columns.length === 0 && (
              <p className={`text-sm ${muted}`}>No lifecycle is defined for this type yet.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-1.5">
          {items.map((it) => (
            <Card key={it._id} item={it} orgName={orgName[it.organizationId || '']}
              onOpen={() => setSelected(it)} wide />
          ))}
          {items.length === 0 && (
            <p className={`text-sm ${muted}`}>
              No tickets match. Clear a filter, or raise one.
            </p>
          )}
        </div>
      )}

      {!loading && more > 0 && (
        <div className="pt-3 flex justify-center">
          <button type="button" onClick={loadMore} disabled={loadingMore} className={secondaryButton}>
            {loadingMore ? 'Loading…' : `Show ${Math.min(more, PAGE_SIZE)} more of ${more}`}
          </button>
        </div>
      )}

      {selected && (
        <Detail
          collection={collection}
          item={selected}
          relationTypes={describe?.relationTypes || []}
          knownAssignees={facets?.assignees || []}
          onClose={() => setSelected(null)}
          onChanged={refresh}
        />
      )}
      {creating && (
        <RaiseTicketDialog
          collection={collection}
          defaultOrgId={query.orgId}
          knownAssignees={facets?.assignees || []}
          onClose={() => setCreating(false)}
          onRaised={() => { setCreating(false); void load(); }}
        />
      )}
    </div>
  );
}

function Card({ item, orgName, onOpen, wide }: {
  item: ItsmItem; orgName?: string; onOpen: () => void; wide?: boolean;
}) {
  // Shared, cached once per page: every card would otherwise ask.
  const warnAt = useSlaPolicy()?.warnAt || 0.75;
  const age = ageLabel(item.createdAt);
  return (
    <button
      type="button"
      onClick={onOpen}
      className={`w-full text-left rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 hover:border-accent ${ring} ${wide ? 'flex items-center gap-3' : ''}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          {item.majorIncident && (
            /* A P1 is still somewhere in its lifecycle — the flag rides on the
               card rather than becoming a column of its own. */
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 font-medium">
              MAJOR
            </span>
          )}
          {item.key && (
            <span className={`font-mono text-[10px] ${muted}`}>{item.key}</span>
          )}
          {/* Priority only once it was assessed: an unassessed ticket carries
              the schema default, which would read as a decision. */}
          {item.priorityBasis && <PriorityPill priority={item.priority} />}
          <SlaBadge item={item} warnAt={warnAt} />
          {item.unassignedService && (
            <span className="px-1.5 py-0.5 rounded text-[10px] border border-amber-700/40 dark:border-amber-400/40 text-amber-700 dark:text-amber-400">
              No service
            </span>
          )}
          {item.signalSource && (
            /* Provenance: a harness-raised item must not look like someone
               typed it. */
            <span className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300">
              {item.signalSource}
            </span>
          )}
        </div>
        <p className="text-sm text-slate-900 dark:text-slate-100 mt-0.5 line-clamp-2">
          {item.title}
        </p>
        <div className={`mt-1 flex items-center gap-x-2.5 gap-y-0.5 flex-wrap text-[11px] ${muted}`}>
          {age && (
            <span className="inline-flex items-center gap-1" title={item.createdAt}>
              <Clock size={10} aria-hidden="true" /><span className="sr-only">Age</span> {age}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Users size={10} aria-hidden="true" /><span className="sr-only">Queue</span>
            {item.ownerGroupName || item.ownerGroup || 'no queue'}
          </span>
          <span className="inline-flex items-center gap-1">
            <UserRound size={10} aria-hidden="true" /><span className="sr-only">Assignee</span>
            {item.assignee || 'unassigned'}
          </span>
          {orgName && <span>{orgName}</span>}
          {(item.relations || []).length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Link2 size={10} aria-hidden="true" /> {(item.relations || []).length}
              <span className="sr-only">relations</span>
            </span>
          )}
        </div>
      </div>
      {wide && item.status && (
        <span className={`shrink-0 text-xs ${muted}`}>{item.status}</span>
      )}
    </button>
  );
}

function Detail({ collection, item, relationTypes, knownAssignees, onClose, onChanged }: {
  collection: ItsmCollection;
  item: ItsmItem;
  relationTypes: string[];
  knownAssignees: string[];
  onClose: () => void;
  onChanged: (updated?: ItsmItem) => void;
}) {
  const [transitions, setTransitions] = useState<ItsmTransition[]>([]);
  const [outcomes, setOutcomes] = useState<string[] | null>(null);
  const [current, setCurrent] = useState(item.status);
  const [busy, setBusy] = useState('');
  const [outcome, setOutcome] = useState('');
  const [windowStart, setWindowStart] = useState(item.changeWindowStart || '');

  const reload = useCallback(async () => {
    try {
      const t = await itsmApi.transitions(collection, item._id);
      setTransitions(t.transitions || []);
      setCurrent(t.current);
      // From the workflow, never a list in this file (PO46-1279).
      setOutcomes(t.outcomes || []);
    } catch {
      setOutcomes([]);       // the panel still renders what it has
    }
  }, [collection, item._id]);

  useEffect(() => { void reload(); }, [reload]);

  // Escape closes the panel, as it closes a dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const needsOutcome = transitions.some((t) => t.requiresOutcome && t.available);

  const apply = async (t: ItsmTransition) => {
    if (t.requiresOutcome && !outcome && !item.outcome) {
      toast.error(`${t.name} ends the ticket, so it needs an outcome. Choose one above the moves.`,
        { duration: 8000 });
      document.getElementById(`outcome-${item._id}`)?.focus();
      return;
    }
    setBusy(t.id);
    try {
      const fields: Record<string, unknown> = {};
      // Sent WITH the move: the server writes them first, so an outcome
      // satisfies the close-out rule in one call instead of two.
      if (outcome) fields.outcome = outcome;
      if (windowStart && windowStart !== item.changeWindowStart) {
        fields.changeWindowStart = windowStart;
      }
      const updated = await itsmApi.transition(collection, item._id, t.id, fields);
      toast.success(`${t.name} → ${t.to}`);
      setOutcome('');
      await reload();
      onChanged(updated);
    } catch (err: unknown) {
      // The server's refusals name the actual blocker ("needs a root-cause
      // analysis", "the window that already failed") — shown as-is.
      toast.error((err instanceof Error && err.message) || 'Could not move it', { duration: 8000 });
    } finally {
      setBusy('');
    }
  };

  const titleId = `ticket-${item._id}-title`;

  return (
    <aside role="complementary" aria-labelledby={titleId}
      className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-panel dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">
      <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {item.key && <span className={`font-mono text-[11px] ${muted}`}>{item.key}</span>}
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {current}
            </span>
            {item.majorIncident && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300">
                MAJOR
              </span>
            )}
            {item.unassignedService && (
              <span className="text-[11px] px-1.5 py-0.5 rounded border border-amber-700/40 dark:border-amber-400/40 text-amber-700 dark:text-amber-400">
                No service
              </span>
            )}
          </div>
          <h2 id={titleId} className="text-sm font-semibold text-slate-900 dark:text-slate-100 mt-1">
            {item.title}
          </h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close the ticket panel"
          className={`text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 shrink-0 rounded ${ring}`}>
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {item.signalSource && (
          <div className={`text-xs ${muted}`}>
            Raised by <strong>{item.signalSource}</strong>
            {item.signalKey && <> · <span className="font-mono">{item.signalKey}</span></>}
          </div>
        )}
        {item.description && (
          <p className="text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-200">{item.description}</p>
        )}

        <AssignSection key={`assign-${item._id}-${item.assignee || ''}`} collection={collection}
          item={item} knownAssignees={knownAssignees} onChanged={onChanged} />

        <PrioritySlaPanel collection={collection} item={item} onChanged={onChanged} />

        <AffectedAssets collection={collection} item={item}
                        onChanged={onChanged} />

        {collection === 'tasks' && (
          <OpsSchedulePanel collection={collection} item={item} onChanged={onChanged} />
        )}

        {/* A close needs an outcome, so the field sits WITH the moves rather
            than in a separate edit step that would make the refusal look
            arbitrary. Its options come from the workflow. */}
        <OutcomeField id={`outcome-${item._id}`} outcomes={outcomes} current={item.outcome}
          value={outcome} onChange={setOutcome} needed={needsOutcome} />

        {collection === 'changes' && (
          <div>
            <label htmlFor={`window-${item._id}`} className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Change window
            </label>
            <input
              id={`window-${item._id}`}
              value={windowStart}
              onChange={(e) => setWindowStart(e.target.value)}
              placeholder="2026-09-08T22:00"
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
            {item.failedWindowStart && (
              <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-400">
                <strong>{item.failedWindowStart}</strong> already failed —
                reworking needs a different window.
              </p>
            )}
          </div>
        )}

        <section aria-labelledby={`moves-${item._id}`}>
          <h3 id={`moves-${item._id}`} className={`${heading} mb-2`}>Moves</h3>
          <div className="space-y-1.5">
            {transitions.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => apply(t)}
                disabled={!t.available || busy === t.id}
                aria-describedby={t.blockedBy ? `why-${item._id}-${t.id}` : undefined}
                className={`w-full text-left px-3 py-2 rounded-lg border text-sm flex items-start gap-2 ${ring} ${
                  t.available
                    ? 'border-slate-300 dark:border-slate-700 hover:border-accent hover:text-accent text-slate-800 dark:text-slate-200'
                    : 'border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 cursor-not-allowed'
                }`}
              >
                {!t.available && <Lock size={12} className="mt-0.5 shrink-0" aria-hidden="true" />}
                <span className="min-w-0">
                  <span className="font-medium">{t.name}</span>
                  <span className={muted}> → {t.to}</span>
                  {t.requiresOutcome && (
                    <span className={`ml-1 text-[11px] ${muted}`}>(ends it; needs an outcome)</span>
                  )}
                  {/* The reason, not just a disabled button. */}
                  {t.blockedBy && (
                    <span id={`why-${item._id}-${t.id}`} className="block text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">
                      {t.blockedBy}
                    </span>
                  )}
                </span>
              </button>
            ))}
            {transitions.length === 0 && (
              <p className={`text-xs ${muted}`}>Nothing can move from here — it is finished.</p>
            )}
          </div>
        </section>

        <RelationsSection collection={collection} item={item} relationTypes={relationTypes}
          onChanged={onChanged} />

        <TimelineSection collection={collection} item={item}
          refreshKey={`${item.status}|${item.assignee || ''}|${item.updatedAt || ''}`} />
      </div>
    </aside>
  );
}


/**
 * What this ticket is about, and what else breaks with it (PO51-1176).
 *
 * The asset is the ANCHOR — it decides the owner the work routes to and whose
 * Operations project the ticket lives in — so it sits at the top of the panel,
 * above the moves. Impact is fetched with it because "what else is affected" is
 * asked at the same moment as "what broke", not on a separate screen.
 */
function AffectedAssets({ collection, item, onChanged }: {
  collection: ItsmCollection;
  item: ItsmItem;
  onChanged: (updated?: ItsmItem) => void;
}) {
  const [assets, setAssets] = useState<ConfigItem[]>([]);
  const [impacts, setImpacts] = useState<Record<string, CiImpact>>({});
  const [adding, setAdding] = useState(false);

  const linked = item.affectedCIs || [];

  useEffect(() => {
    cmdbApi.list().then((d) => setAssets(d.items || [])).catch(() => setAssets([]));
  }, []);

  useEffect(() => {
    // Only for what is linked: the impact of every asset in the CMDB is not a
    // question anyone asked.
    linked.forEach((id) => {
      if (impacts[id]) return;
      cmdbApi.impact(id).then((i) => setImpacts((m) => ({ ...m, [id]: i })))
        .catch(() => {});
    });
  }, [linked.join(','), impacts]);

  const attach = async (ciId: string) => {
    if (!ciId || linked.includes(ciId)) return;
    try {
      const updated = await itsmApi.update(collection, item._id,
        { affectedCIs: [...linked, ciId] });
      toast.success('Asset attached');
      setAdding(false);
      onChanged(updated);
    } catch (err: any) {
      toast.error(err.message || 'Could not attach it');
    }
  };

  const detach = async (ciId: string) => {
    try {
      const updated = await itsmApi.update(collection, item._id,
        { affectedCIs: linked.filter((i) => i !== ciId) });
      onChanged(updated);
    } catch (err: any) {
      toast.error(err.message || 'Could not detach it');
    }
  };

  const byId: Record<string, ConfigItem> = {};
  assets.forEach((a) => { byId[a._id] = a; });

  return (
    <div>
      <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-1.5">
        <Boxes size={12} /> Affected assets
        {item.ownerGroup && (
          <span className="ml-auto text-[10px] text-gray-400">
            owner: {item.ownerGroupName || item.ownerGroup}
          </span>
        )}
      </h3>

      {linked.length === 0 && !adding && (
        <p className="text-xs text-amber-600 dark:text-amber-500">
          Nothing attached — this ticket has no owner to route to and no impact
          to assess.
        </p>
      )}

      <div className="space-y-1.5">
        {linked.map((id) => {
          const ci = byId[id];
          const impact = impacts[id];
          return (
            <div key={id} className="rounded-lg border border-gray-200 dark:border-gray-700 px-2.5 py-2">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-900 dark:text-gray-100">
                    {ci?.name || id}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {ci?.ciType}{ci?.ownerGroup ? ` · ${ci.ownerGroup}` : ''}
                  </p>
                  {impact && impact.impactCount > 0 && (
                    /* The number that changes how an incident is handled. */
                    <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-500">
                      {impact.impactCount} other asset
                      {impact.impactCount === 1 ? '' : 's'} affected
                      {impact.affectedOwnerGroups.length > 0 &&
                        ` · tell ${impact.affectedOwnerGroups.join(', ')}`}
                    </p>
                  )}
                </div>
                <button onClick={() => detach(id)}
                  className="shrink-0 text-gray-300 dark:text-gray-600 hover:text-red-500">
                  <X size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {adding ? (
        <select autoFocus onChange={(e) => attach(e.target.value)}
          className="mt-2 w-full px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
          <option value="">— pick an asset —</option>
          {assets.filter((a) => !linked.includes(a._id)).map((a) => (
            <option key={a._id} value={a._id}>{a.name} · {a.ciType}</option>
          ))}
        </select>
      ) : (
        <button onClick={() => setAdding(true)}
          className="mt-2 text-xs text-accent">+ Attach an asset</button>
      )}
    </div>
  );
}
